import { useEffect, useRef, useState } from 'react';
import { StaticValue, useEditing } from './Field';

/**
 * A comma-separated list of tags, typed as text.
 *
 * It exists because the obvious way to write this is broken in a way that is
 * hard to see and impossible to work around:
 *
 * ```tsx
 * <TextInput
 *   value={draft.tags.join(', ')}
 *   onChange={(v) => setDraft({ ...draft, tags: v.split(',').map((t) => t.trim()) })}
 * />
 * ```
 *
 * The value shown is recomputed from the parsed array on every keystroke, so
 * any character the parse discards can never be typed. Press space after
 * "senior" and the segment "senior " is trimmed to "senior", the value goes
 * back to "senior", and the space is gone before the next key arrives. The
 * field does not reject spaces or warn about them -- it silently refuses to
 * accept one, which reads as the application insisting on "senior-tech".
 *
 * The rule this encodes: **while somebody is typing, the text they have typed
 * is the truth.** The parsed array is derived from it, never the other way
 * round. The parent is still told about every change, so saving needs no
 * special handling.
 */

const parse = (text: string): string[] =>
  text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

const same = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export function TagsInput({
  value,
  onChange,
  placeholder,
}: {
  value: readonly string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}) {
  const editing = useEditing();
  const [text, setText] = useState(() => value.join(', '));

  /*
   * What we last handed upwards. Anything arriving that differs from it came
   * from somewhere else -- a different record selected, or the row reloaded
   * after a save -- and only then should the text be replaced underneath the
   * cursor.
   */
  const emitted = useRef<readonly string[]>(value);

  useEffect(() => {
    if (!same(value, emitted.current)) {
      emitted.current = value;
      setText(value.join(', '));
    }
  }, [value]);

  if (!editing) return <StaticValue>{value.join(', ')}</StaticValue>;

  return (
    <input
      className="field"
      value={text}
      placeholder={placeholder}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        const tags = parse(next);
        emitted.current = tags;
        onChange(tags);
      }}
    />
  );
}
